type ParsedDefineFormat = {
    fullMatch: string;
    mainPart: string;
    additionalFunction: string | null;
};
type ParsedDefineFormatAdditionalFunction = (value: string) => string;

export function getUserNameWithGroup(user: { displayName?: string; divisionName?: string }): string | undefined {
    if (user.divisionName) {
        return `${user.displayName}(${user.divisionName})`;
    }

    return user.displayName;
}

/**
 * standard form 에 사용되는 define의 format 을 파싱하여 사용하기 위한 타입
 * 예를 들어, "{value|comma}{unit}" 문자열은 "value"라는 주요 부분과 "comma"라는 추가적인 형식 지시어로 분리됩니다.
 * 이 함수는 주어진 format 문자열에서 이러한 구조를 추출하여 파싱된 형식의 배열로 반환합니다.
 *
 * @param formatString 파싱할 format 문자열. 이 문자열은 중괄호로 묶인 하나 이상의 형식 지정자를 포함합니다.
 * @returns {ParsedDefineFormat[]} 파싱된 format 요소들을 포함하는 배열. 각 요소는 `fullMatch`, `mainPart`, `additionalFunction` 속성을 가집니다.
 */
export function parseDefineFormat(formatString: string): ParsedDefineFormat[] {
    const formatPattern = /\{([^}]+)\}/g;
    let match: RegExpExecArray | null;
    const parsedFormats: ParsedDefineFormat[] = [];

    while ((match = formatPattern.exec(formatString)) !== null) {
        const [fullMatch, content] = match;
        const parts = content.split('|');
        const mainPart = parts[0];
        const additionalFunction = parts.length > 1 ? parts[1] : null;

        parsedFormats.push({
            fullMatch,
            mainPart,
            additionalFunction,
        });
    }

    return parsedFormats;
}

/**
 * 주어진 입력 데이터와 추가 함수를 사용하여 'format' 정의에 따라 값을 형식화합니다.
 * 이 함수는 'format' 문자열 내에 지정된 형식 지시자에 따라 'value'와 'unit'을 적절히 대체합니다.
 * 필요한 경우, 추가 함수 (예: 콤마 추가)를 적용하여 최종 형식화된 문자열을 생성합니다.
 *
 * @param input - 'format' 문자열, 대체할 'value' 및 'unit'을 포함하는 객체
 * @param additionalFunctions - 'format' 문자열 내의 추가 형식 지시자에 적용할 함수들
 * @returns 형식화된 문자열
 */
export function formatDefineValue(input: { format: string; value: string; unit?: string }, additionalFunctions: { [key: string]: ParsedDefineFormatAdditionalFunction } = {}): string {
    const parsedFormats = parseDefineFormat(input.format);
    let formattedValue = input.format;

    for (const format of parsedFormats) {
        let replacementValue = '';

        switch (format.mainPart) {
            case 'value':
                if (format.additionalFunction) {
                    const func = additionalFunctions[format.additionalFunction];
                    if (!func) {
                        throw new Error(`Additional function '${format.additionalFunction}' is required but not provided`);
                    }
                    replacementValue = func(input.value);
                } else {
                    replacementValue = input.value;
                }
                break;
            case 'unit':
                if (input.unit === undefined || input.unit === null) {
                    throw new Error(`Unit is not defined for format: ${input.format}`);
                }
                replacementValue = input.unit;
                break;
            default:
                // 처리할 수 없는 형식
                throw new Error(`Unsupported format part: ${format.mainPart}`);
        }

        formattedValue = formattedValue.replace(format.fullMatch, replacementValue);
    }

    return formattedValue;
}

/**
 * 주어진 텍스트에서 일치하는 부분을 하이라이트하는 함수
 * @param {string} text - 원본 텍스트
 * @param {string} match - 일치하는 부분 문자열
 * @param {string} colorClass - 하이라이트할 색상 클래스 (Tailwind CSS 클래스 등)
 * @returns {string} - 하이라이트된 HTML 문자열
 */
export function highlightMatch(text: string, match: string, colorClass: string) {
    if (!text || !match || !colorClass) return text;
    const regex = new RegExp(`(${match})`, 'gi');
    return text.replace(regex, `<span class="${colorClass}">$1</span>`);
}

const URL_PATTERN = /https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"]/g;

/**
 * 텍스트 내 URL을 클릭 가능한 앵커 태그(`<a>`)로 변환하는 함수
 * HTML escape는 수행하지 않으므로, {@html} 디렉티브와 함께 사용할 경우 반드시 호출 전에 escape 처리를 해야 합니다.
 * @param {string} text - 원본 텍스트 (필요 시 사전에 escape 처리된 텍스트)
 * @returns {string} - URL이 앵커 태그로 변환된 문자열
 */
export function linkifyText(text: string): string {
    if (!text) return '';

    return text.replace(URL_PATTERN, url => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">${url}</a>`;
    });
}
